import { useEffect, useMemo, useRef, useState } from "react"
import { useIntl } from "react-intl"
import { useLocation, useNavigate } from "react-router-dom"

import classnames from "classnames"

import { ICONS } from "@doktor-se/bones-ui"

import { featureFlags } from "config"
import { AmplitudeEventMap } from "lib/amplitude/events"
import { useStaffAmplitudeLogger } from "lib/amplitude/hooks"
import { useAppSelector } from "lib/hooks"
import { selectSnoozedConversations } from "reducers/conversations/conversations.reducer"

import NavigationHeader from "./components/Header"
import LogoutDialog from "./components/LogoutDialog"
import MenuItem from "./components/MenuItem"
import NavigationUserInfo from "./components/UserInfo"
import CreateConversation from "components/CreateConversation/CreateConversation"

import styles from "./Navigation.module.scss"

const isRouteOrSubActive = (currentPath: string, ...routes: (string | RegExp)[]) =>
  routes.some(route => (typeof route === "string" ? currentPath === route : route.test(currentPath)))

interface NavigationProps {
  showInboxNotificationDot?: boolean
}

const Navigation = ({ showInboxNotificationDot = false }: NavigationProps) => {
  const intl = useIntl()
  const location = useLocation()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const [logoutOpen, setLogoutOpen] = useState(false)
  const [toggleAddConv, setToggleAddConv] = useState(false)
  const beamerRef = useRef<HTMLButtonElement>(null)
  const { roles } = useAppSelector(state => state.auth)
  const snoozed = useAppSelector(selectSnoozedConversations)

  const createErrandRef = useRef<HTMLButtonElement>(null)
  const [offset, setOffset] = useState({
    left: 0,
    bottom: 0
  })

  const calculateOffset = () => {
    const { bottom } = createErrandRef.current!.getBoundingClientRect()
    setOffset({ left: 145, bottom: bottom + 145 })
  }

  const logAmplitude = useStaffAmplitudeLogger()

  const toggleAddConvDialog = (value: boolean) => {
    calculateOffset()
    // if set to true we send amplitude event
    value && logAmplitude("carealot.navigation.create_errand_pressed")
    setToggleAddConv(value)
  }

  const showSupportChat = useMemo(() => {
    if (featureFlags.has("freshdesk_chat")) {
      return true
    }
    return false
  }, [])

  const showBulkMessagesTab = useMemo(() => {
    if (featureFlags.has("bulk_messages")) {
      if (roles.includes("cherry_pick")) {
        return true
      }
    }
    return false
  }, [roles])

  const showUploadTab = useMemo(() => {
    if (roles.includes("listing_uploader")) {
      return true
    }
    return false
  }, [roles])

  const openLogoutDialog = () => {
    logAmplitude("carealot.navigation.log_out_pressed")
    setLogoutOpen(true)
  }

  const toggleFreshdeskChat = () => {
    logAmplitude("carealot.navigation.support_chat_pressed")
    const fcWidget = (window as any).fcWidget
    if (!!fcWidget) {
      if (fcWidget.isOpen()) {
        fcWidget.close()
      } else {
        fcWidget.open()
      }
    }
  }

  useEffect(() => {
    const button = beamerRef.current
    const handleClick = () => logAmplitude("carealot.navigation.whats_new_pressed")
    if (featureFlags.has("beamer")) {
      button?.classList.add("beamerTrigger")
      button?.addEventListener("click", handleClick, true)
    }
    return () => {
      button?.classList.remove("beamerTrigger")
      button?.removeEventListener("click", handleClick, true)
    }
  }, [logAmplitude])

  const changePage = (path: string, amplitudeEventType: keyof AmplitudeEventMap) => {
    logAmplitude(amplitudeEventType)
    navigate(path)
  }

  const toggleCollapse = () => setCollapsed(!collapsed)

  return (
    <div className={classnames(styles.navigation, { [styles.expanded]: !collapsed, [styles.collapsed]: collapsed })}>
      <NavigationHeader collapsed={collapsed} toggleCollapse={toggleCollapse} />
      <div className={styles.sections}>
        <div className={styles.section}>
          <MenuItem
            collapsed={collapsed}
            active={isRouteOrSubActive(location.pathname, "/", "/overview", "/view-errand", /view-errand\/.*/)}
            onClick={() => changePage("/overview", "carealot.navigation.inbox_pressed")}
            Icon={ICONS.Inbox}
            label={intl.formatMessage({ id: "label.inbox" })}
            notification={showInboxNotificationDot}
          />
          <MenuItem
            collapsed={collapsed}
            active={location.pathname === "/paused"}
            onClick={() => changePage("/paused", "carealot.navigation.paused_pressed")}
            Icon={ICONS.Snooze}
            label={`${intl.formatMessage({ id: "label.paused" })} (${snoozed.length})`}
          />
          <MenuItem
            collapsed={collapsed}
            active={location.pathname === "/booking"}
            onClick={() => changePage("/booking", "carealot.navigation.booking_pressed")}
            Icon={ICONS.CalendarToday}
            label={intl.formatMessage({ id: "label.booking" })}
          />
          {!!showBulkMessagesTab && (
            <MenuItem
              collapsed={collapsed}
              active={location.pathname === "/bulk"}
              onClick={() => changePage("/bulk", "carealot.navigation.bulk_messages_pressed")}
              Icon={ICONS.Mail}
              label={intl.formatMessage({ id: "bulk_messages.header" })}
            />
          )}
        </div>
        <div className={styles.section}>
          <MenuItem
            collapsed={collapsed}
            menuItemRef={createErrandRef}
            onClick={() => toggleAddConvDialog(!toggleAddConv)}
            Icon={ICONS.Add}
            label={intl.formatMessage({ id: "label.create_errand" })}
          />
          <MenuItem
            collapsed={collapsed}
            active={location.pathname.startsWith("/search")}
            onClick={() => changePage("/search", "carealot.navigation.search_errands_pressed")}
            Icon={ICONS.Search}
            label={intl.formatMessage({ id: "label.search_errand" })}
          />
          <MenuItem
            collapsed={collapsed}
            active={location.pathname === "/dial"}
            onClick={() => changePage("/dial", "carealot.navigation.call_number_pressed")}
            Icon={ICONS.Dialpad}
            label={intl.formatMessage({ id: "label.call_number" })}
          />
          <MenuItem
            collapsed={collapsed}
            active={location.pathname === "/phrases"}
            onClick={() => changePage("/phrases", "carealot.navigation.phrases_pressed")}
            Icon={ICONS.TextSnippet}
            label={intl.formatMessage({ id: "phrases.toolbar.phrases.label" })}
          />
        </div>
        <div>
          {!!showSupportChat && (
            <MenuItem
              collapsed={collapsed}
              onClick={toggleFreshdeskChat}
              Icon={ICONS.Chat}
              label={intl.formatMessage({ id: "label.support_chat" })}
            />
          )}
          {!!showUploadTab && (
            <MenuItem
              collapsed={collapsed}
              label={intl.formatMessage({ id: "label.upload" })}
              Icon={ICONS.Upload}
              onClick={() => changePage("/upload", "carealot.navigation.upload_pressed")}
              active={location.pathname === "/upload"}
            />
          )}
          <MenuItem
            collapsed={collapsed}
            active={location.pathname === "/settings"}
            onClick={() => changePage("/settings", "carealot.navigation.settings_pressed")}
            Icon={ICONS.Settings}
            label={intl.formatMessage({ id: "label.settings" })}
          />
          {featureFlags.has("beamer") && (
            <MenuItem
              collapsed={collapsed}
              menuItemRef={beamerRef}
              onClick={() => {}}
              Icon={ICONS.StarOutline}
              label={intl.formatMessage({ id: "label.news" })}
            />
          )}
          <MenuItem
            collapsed={collapsed}
            active={location.pathname === "/help"}
            onClick={() => changePage("/help", "carealot.navigation.faq_pressed")}
            Icon={ICONS.Info}
            label={intl.formatMessage({ id: "label.help" })}
          />

          <NavigationUserInfo collapsed={collapsed} />

          <MenuItem
            collapsed={collapsed}
            onClick={openLogoutDialog}
            Icon={ICONS.Login}
            label={intl.formatMessage({ id: "label.logout" })}
            additionalButtonStyles={styles.logoutButton}
          />
        </div>
      </div>
      {featureFlags.has("create_conversation") && (
        <CreateConversation
          show={toggleAddConv}
          hide={() => setToggleAddConv(false)}
          leftOffset={offset.left}
          bottomOffset={offset.bottom}
        />
      )}
      <LogoutDialog isOpen={logoutOpen} onClose={() => setLogoutOpen(false)} />
    </div>
  )
}

export default Navigation
